/**
 * testTemplates.js — MVP test catalog (v1).
 * ============================================================================
 *
 * Only the 7 approved MVP tests. Each template is metadata only — protocol
 * timing for audio tests lives in /protocols. Categories: Kondition, Snabbhet,
 * Agility.
 *
 * resultType:
 *   'audio'   → live audio runner (level/shuttle/distance)
 *   'timer'   → countdown timer + manual distance entry (Cooper)
 *   'manual'  → manual time entry per attempt
 *
 * bestResultLogic: 'min' (lower is better) | 'max' (higher is better)
 */
import yoyoIR1Protocol from '../protocols/yoyoIR1Protocol';
import beepTestProtocol from '../protocols/beepTestProtocol';

export const TEST_CATEGORIES = ['Kondition', 'Snabbhet', 'Agility'];

export const testTemplates = [
  // ── AUDIO ────────────────────────────────────────────────────────────────
  {
    id: 'yoyo_ir1',
    name: 'Yo-Yo IR1 / IRTL1',
    category: 'Kondition',
    type: 'audio',
    protocolId: 'yoyo_ir1',
    description: 'Intermittent uthållighetstest med 2×20 m sträckor och 10 s aktiv återhämtning.',
    purpose: 'Mäter förmågan att upprepa högintensivt löparbete med återhämtning — central för fotboll/futsal.',
    equipment: ['Kon × 3 (start, vändning 20 m, återhämtning 5 m)', 'Ljud/högtalare', '20 m + 5 m markerat'],
    setupInstructions: 'Markera 20 m mellan startlinje och vändlinje, samt 5 m bakom startlinjen för återhämtning.',
    executionInstructions: 'Spelaren springer 20 m ut och 20 m tillbaka i takt med pipen, joggar 5+5 m under 10 s återhämtning. Tappar spelaren takten två gånger är testet slut.',
    resultType: 'audio',
    unit: 'm',
    attempts: 1,
    bestResultLogic: 'max',
    audioRequired: true,
    timerRequired: false,
    manualInputRequired: false,
    recommendedAgeGroups: ['U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Ljud'],
  },
  {
    id: 'beep_test',
    name: 'Beep test 20 m Léger 8.5',
    category: 'Kondition',
    type: 'audio',
    protocolId: 'beep_test',
    description: '20 m Multistage Fitness Test (Léger). Start 8.5 km/h, +0.5 km/h per nivå.',
    purpose: 'Skattar maximal aerob kapacitet (VO₂max) via progressivt löpartest.',
    equipment: ['Kon × 2 (20 m isär)', 'Ljud/högtalare', '20 m markerat'],
    setupInstructions: 'Markera 20 m mellan två linjer.',
    executionInstructions: 'Spelaren springer 20 m fram och tillbaka i takt med pipen. Hastigheten ökar varje nivå. Testet slutar när spelaren inte når linjen i takt två gånger i rad.',
    resultType: 'audio',
    unit: 'nivå',
    attempts: 1,
    bestResultLogic: 'max',
    audioRequired: true,
    timerRequired: false,
    manualInputRequired: false,
    recommendedAgeGroups: ['U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Ljud'],
  },

  // ── MANUAL: timer ──────────────────────────────────────────────────────────
  {
    id: 'cooper',
    protocolVersion: 'trainnova-cooper-manual-v1',
    name: 'Cooper-test',
    category: 'Kondition',
    type: 'timer',
    description: 'Spring så långt som möjligt på 12 minuter.',
    purpose: 'Skattar aerob uthållighet via tillryggalagd distans på 12 min.',
    equipment: ['Bana med känd distans/varv', 'Ljud/visselpipa'],
    setupInstructions: 'Mät upp en bana (t.ex. 400 m varv eller koner var 50 m).',
    executionInstructions: 'Spelarna springer kontinuerligt i 12 minuter. Notera distans i meter per spelare när tiden går ut.',
    resultType: 'timer',
    unit: 'm',
    durationSeconds: 12 * 60,
    attempts: 1,
    bestResultLogic: 'max',
    audioRequired: false,
    timerRequired: true,
    manualInputRequired: true,
    recommendedAgeGroups: ['U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Timer', 'Manuell'],
  },

  // ── MANUAL: time entry ──────────────────────────────────────────────────────
  {
    id: 'sprint_10m',
    protocolVersion: 'trainnova-sprint_10m-manual-v1',
    name: '10 m sprint',
    category: 'Snabbhet',
    type: 'manual',
    description: 'Maximal acceleration över 10 meter.',
    purpose: 'Mäter acceleration/snabbhet på kort distans.',
    equipment: ['Tidtagarur eller tidsgrindar', '10 m markerat'],
    setupInstructions: 'Markera start och mål 10 m isär.',
    executionInstructions: 'Spelaren startar från stillastående och springer maximalt. Ta tid i sekunder. 1–3 försök, bästa tiden räknas.',
    resultType: 'manual',
    unit: 's',
    attempts: 3,
    bestResultLogic: 'min',
    audioRequired: false,
    timerRequired: false,
    manualInputRequired: true,
    recommendedAgeGroups: ['U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Manuell', 'Tid'],
  },
  {
    id: 'sprint_20m',
    protocolVersion: 'trainnova-sprint_20m-manual-v1',
    name: '20 m sprint',
    category: 'Snabbhet',
    type: 'manual',
    description: 'Maximal sprint över 20 meter.',
    purpose: 'Mäter acceleration och löpsnabbhet.',
    equipment: ['Tidtagarur eller tidsgrindar', '20 m markerat'],
    setupInstructions: 'Markera start och mål 20 m isär.',
    executionInstructions: 'Spelaren startar från stillastående och springer maximalt. Ta tid i sekunder. 1–3 försök, bästa tiden räknas.',
    resultType: 'manual',
    unit: 's',
    attempts: 3,
    bestResultLogic: 'min',
    audioRequired: false,
    timerRequired: false,
    manualInputRequired: true,
    recommendedAgeGroups: ['U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Manuell', 'Tid'],
  },
  {
    id: 'agility_505',
    protocolVersion: 'trainnova-agility_505-manual-v1',
    name: '505 Agility',
    category: 'Agility',
    type: 'manual',
    description: 'Riktningsförändring 180° mätt över 5 m in/ut.',
    purpose: 'Mäter förmåga till snabb inbromsning och riktningsförändring per sida.',
    equipment: ['Tidtagarur eller tidsgrindar', '15 m bana med vändlinje'],
    setupInstructions: 'Markera 10 m inlöp + 5 m mätsträcka till vändlinjen.',
    executionInstructions: 'Spelaren accelererar, vänder 180° vid linjen och sprintar tillbaka. Mät vänster och höger sida separat. Bästa tid per sida räknas.',
    resultType: 'manual',
    unit: 's',
    attempts: 4,
    bestResultLogic: 'min',
    hasSides: true,
    audioRequired: false,
    timerRequired: false,
    manualInputRequired: true,
    recommendedAgeGroups: ['U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Manuell', 'Tid'],
  },
  {
    id: 't_test',
    protocolVersion: 'trainnova-t_test-manual-v1',
    name: 'T-test',
    category: 'Agility',
    type: 'manual',
    description: 'Agilitytest i T-form: framåt, sidled, sidled, bakåt.',
    purpose: 'Mäter agility med fram-/sid-/bakåtrörelse.',
    equipment: ['Kon × 4 i T-form', 'Tidtagarur'],
    setupInstructions: 'Ställ konerna i T-form (10 m framåt, 5 m åt vardera sidan).',
    executionInstructions: 'Spelaren sprintar fram, sidoförflyttar, springer baklänges enligt T-mönstret. Ta tid i sekunder. Flera försök, bästa tiden räknas.',
    resultType: 'manual',
    unit: 's',
    attempts: 3,
    bestResultLogic: 'min',
    audioRequired: false,
    timerRequired: false,
    manualInputRequired: true,
    recommendedAgeGroups: ['U14', 'U15', 'U16', 'U17', 'U19', 'Senior'],
    sportRelevance: 'both',
    tags: ['Manuell', 'Tid'],
  },
];

export const PROTOCOLS = {
  yoyo_ir1: yoyoIR1Protocol,
  beep_test: beepTestProtocol,
};

export const getTemplate = (id) => testTemplates.find((t) => t.id === id) || null;
export const getProtocol = (protocolId) => PROTOCOLS[protocolId] || null;

/** Compute the best attempt value given a list of numbers and logic. */
export function computeBest(values, logic = 'min') {
  const nums = (values || []).filter(v => v !== null && v !== undefined && String(v).trim() !== '').map(Number).filter(Number.isFinite);
  if (!nums.length) return null;
  return logic === 'max' ? Math.max(...nums) : Math.min(...nums);
}

export default testTemplates;
